import React, {useContext, useEffect, useState} from 'react';
import axios from 'axios';
import InputMask from 'react-input-mask';
import moment from 'moment';
import {Breadcrumb, Spinner} from 'react-bootstrap';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {API_URL} from '../../constants';
import withAuth from '../../auth';
import {AuthContext} from '../AuthContext';
import NPPlacesInput from '../google_map/NPPlacesInput';
import {GoogleMapsProvider} from '../google_map/GoogleMapsContext';

const JSON_FIELDS = [
    {
        name: 'icons',
        label: 'Icons',
        help: 'Logo, favicon, and notification icon URLs.',
    },
    {
        name: 'images',
        label: 'Images',
        help: 'Background and other branded image URLs.',
    },
    {
        name: 'theme',
        label: 'Theme',
        help: 'Brand colors and other visual theme values.',
    },
    {
        name: 'default_map_point',
        label: 'Default Map Point',
        help: 'Default map latitude and longitude.',
    },
    {
        name: 'links',
        label: 'Links',
        help: 'Company, privacy, terms, and web-app links.',
    },
    {
        name: 'settings',
        label: 'Settings',
        help: 'Additional White Label settings.',
    },
];

const initialFormData = {
    name: '',
    address: '',
    invoice_support_phone: '',
    invoice_support_email: '',
    cancel_fee: '',
    invoice_terms_days: '',
    allow_card_payments: false,
    allow_invoice_payments: false,
};

const formatJson = (value) => JSON.stringify(value ?? null, null, 2);

const formatPhone = (value) => {
    const phone = String(value ?? '').trim();
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    if (digits.length === 11) {
        return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    return phone;
};

const getCompanyFromResponse = (response) => (
    response?.data?.data
    ?? response?.data?.whitelabel
    ?? response?.data
);

const getErrorMessage = (error, fallback) => {
    if (error?.response?.data?.errors) {
        return Object.values(error.response.data.errors).flat().join(' ');
    }

    return error?.response?.data?.message || fallback;
};

const parseJsonField = (value, label) => {
    if (!value.trim()) return null;

    try {
        return JSON.parse(value);
    } catch (_error) {
        throw new Error(`${label} must contain valid JSON.`);
    }
};

export const buildWhitelabelSectionPayload = (section, formData, jsonFields) => {
    if (section === 'company') {
        return {name: formData.name};
    }

    if (section === 'payments') {
        return {
            address: formData.address,
            invoice_support_email: formData.invoice_support_email,
            invoice_support_phone: formData.invoice_support_phone,
            cancel_fee: formData.cancel_fee === '' ? null : Number(formData.cancel_fee),
            invoice_terms_days: formData.invoice_terms_days === '' ? null : Number(formData.invoice_terms_days),
            allow_card_payments: Boolean(formData.allow_card_payments),
            allow_invoice_payments: Boolean(formData.allow_invoice_payments),
        };
    }

    if (section === 'branding') {
        return Object.fromEntries(JSON_FIELDS.map(({name, label}) => [
            name,
            parseJsonField(jsonFields[name], label),
        ]));
    }

    throw new Error('Unknown White Label details section.');
};

export const WhitelabelCompany = () => {
    const {whitelabelId} = useParams();
    const navigate = useNavigate();
    const {token, setToken} = useContext(AuthContext);
    const [company, setCompany] = useState(null);
    const [formData, setFormData] = useState(initialFormData);
    const [jsonFields, setJsonFields] = useState(() => Object.fromEntries(
        JSON_FIELDS.map(({name}) => [name, 'null'])
    ));
    const [loading, setLoading] = useState(true);
    const [savingSection, setSavingSection] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        let active = true;

        const fetchCompany = async () => {
            setLoading(true);
            setError('');

            try {
                const response = await axios.get(`${API_URL}/api/admin/whitelabels/${whitelabelId}`, {
                    headers: {Authorization: `Bearer ${token}`},
                });
                if (!active) return;

                const loadedCompany = getCompanyFromResponse(response);
                setCompany(loadedCompany);
                setFormData({
                    name: loadedCompany.name ?? '',
                    address: loadedCompany.address ?? '',
                    invoice_support_phone: formatPhone(loadedCompany.invoice_support_phone),
                    invoice_support_email: loadedCompany.invoice_support_email ?? '',
                    cancel_fee: loadedCompany.cancel_fee ?? '',
                    invoice_terms_days: loadedCompany.invoice_terms_days ?? '',
                    allow_card_payments: Boolean(loadedCompany.allow_card_payments),
                    allow_invoice_payments: Boolean(loadedCompany.allow_invoice_payments),
                });
                setJsonFields(Object.fromEntries(
                    JSON_FIELDS.map(({name}) => [name, formatJson(loadedCompany[name])])
                ));
            } catch (requestError) {
                if (!active) return;

                if (requestError.response?.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                    return;
                }

                setError(getErrorMessage(requestError, 'Failed to load the White Label company.'));
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchCompany();

        return () => {
            active = false;
        };
    }, [navigate, setToken, token, whitelabelId]);

    const handleChange = (event) => {
        const {name, value, type, checked} = event.target;
        setFormData((current) => ({
            ...current,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleAddressChange = (address) => {
        setFormData((current) => ({...current, address}));
    };

    const handleSectionSubmit = async (event, section) => {
        event.preventDefault();
        setError('');
        setSuccess('');

        let payload;
        try {
            payload = buildWhitelabelSectionPayload(section, formData, jsonFields);
        } catch (validationError) {
            setError(validationError.message);
            return;
        }

        setSavingSection(section);
        try {
            const response = await axios.put(
                `${API_URL}/api/admin/whitelabels/${whitelabelId}`,
                payload,
                {headers: {Authorization: `Bearer ${token}`}},
            );
            const updatedCompany = getCompanyFromResponse(response);

            if (updatedCompany?.id) {
                setCompany(updatedCompany);
            } else {
                setCompany((current) => ({...current, ...payload}));
            }
            setSuccess(response.data.message || 'White Label company updated successfully.');
        } catch (requestError) {
            if (requestError.response?.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
                return;
            }

            setError(getErrorMessage(requestError, 'Failed to update the White Label company.'));
        } finally {
            setSavingSection(null);
        }
    };

    const managers = company?.managers || [];
    const subscription = company?.subscription;
    const detailsTitle = `Whitelabel Details ${whitelabelId}`;
    const renderUpdateButton = (section, label) => (
        <button
            type="submit"
            className="btn btn-primary btn-sm"
            aria-label={`Update ${label}`}
            disabled={savingSection !== null}
        >
            {savingSection === section ? (
                <>
                    <Spinner as="span" animation="border" size="sm" aria-hidden="true"/>
                    <span className="ml-2">Updating...</span>
                </>
            ) : 'Update'}
        </button>
    );

    return (
        <div className="container-fluid mt-3 mb-4">
            <Breadcrumb>
                <Breadcrumb.Item linkAs={Link} linkProps={{to: '/whitelabels'}}>Whitelabels</Breadcrumb.Item>
                <Breadcrumb.Item active>{detailsTitle}</Breadcrumb.Item>
            </Breadcrumb>

            <h1 className="text-center">{detailsTitle}</h1>

            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            {success && <div className="alert alert-success" role="status">{success}</div>}

            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{height: '60vh'}}>
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </div>
            ) : company ? (
                <div className="row justify-content-center">
                    <div className="col-xl-10">
                        <div className="row">
                            <div className="col-lg-6">
                                <form onSubmit={(event) => handleSectionSubmit(event, 'company')}>
                                    <div className="card mb-3">
                                        <div className="card-header d-flex justify-content-between align-items-center">
                                            <span className="font-weight-bold">Company</span>
                                            {renderUpdateButton('company', 'Company')}
                                        </div>
                                        <div className="card-body">
                                            <div className="form-group mb-0">
                                                <label htmlFor="name">Company Name</label>
                                                <input
                                                    id="name"
                                                    name="name"
                                                    className="form-control"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </form>

                                <div className="card mb-3">
                                    <div className="card-header font-weight-bold">Account Context</div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6 mb-3 mb-md-0">
                                                <h2 className="h6">Managers ({company.managers_count ?? managers.length})</h2>
                                                {managers.length > 0 ? managers.map((manager) => (
                                                    <div key={manager.id} className="mb-2">
                                                        <strong>{manager.name}</strong>
                                                        <div><a href={`mailto:${manager.email}`}>{manager.email}</a></div>
                                                        {manager.phone && <div>{manager.phone}</div>}
                                                    </div>
                                                )) : <span className="text-muted">No managers</span>}
                                            </div>
                                            <div className="col-md-6">
                                                <h2 className="h6">Subscription</h2>
                                                {subscription ? (
                                                    <>
                                                        <div className="text-capitalize">{subscription.status?.replace(/_/g, ' ')}</div>
                                                        {subscription.plan?.name && <div>{subscription.plan.name}</div>}
                                                        {subscription.current_period_end && (
                                                            <small className="text-muted">
                                                                Through {moment(subscription.current_period_end).format('YYYY-MM-DD')}
                                                            </small>
                                                        )}
                                                    </>
                                                ) : <span className="text-muted">No subscription</span>}
                                            </div>
                                        </div>
                                        {(company.created_at || company.updated_at) && <hr/>}
                                        <div className="row small text-muted">
                                            {company.created_at && (
                                                <div className="col-md-6">
                                                    Created {moment(company.created_at).format('YYYY-MM-DD HH:mm')}
                                                </div>
                                            )}
                                            {company.updated_at && (
                                                <div className="col-md-6">
                                                    Updated {moment(company.updated_at).format('YYYY-MM-DD HH:mm')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <form
                                className="col-lg-6"
                                onSubmit={(event) => handleSectionSubmit(event, 'payments')}
                            >
                                <div className="card mb-3">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <span className="font-weight-bold">Payments and Billing Documents</span>
                                        {renderUpdateButton('payments', 'Payments and Billing Documents')}
                                    </div>
                                    <div className="card-body">
                                        <div className="form-group">
                                            <label htmlFor="invoice_support_email">
                                                Email for Invoices and Receipts
                                            </label>
                                            <input
                                                id="invoice_support_email"
                                                name="invoice_support_email"
                                                type="email"
                                                className="form-control"
                                                value={formData.invoice_support_email}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="address">Address</label>
                                            <NPPlacesInput
                                                inputId="address"
                                                inputName="address"
                                                value={formData.address}
                                                placeholder="Start typing the company address"
                                                onAddressChange={handleAddressChange}
                                                onResultSelected={handleAddressChange}
                                            />
                                            <small id="address-help" className="form-text text-muted">
                                                This address is used on invoices and receipts sent to customers.
                                            </small>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="invoice_support_phone">
                                                Phone for Invoices and Receipts
                                            </label>
                                            <InputMask
                                                id="invoice_support_phone"
                                                name="invoice_support_phone"
                                                type="tel"
                                                mask="+9 (999) 999-9999"
                                                maskChar={null}
                                                placeholder="+1 (204) 555-0199"
                                                className="form-control"
                                                value={formData.invoice_support_phone}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group col-sm-6">
                                                <label htmlFor="cancel_fee">Cancellation Fee</label>
                                                <input
                                                    id="cancel_fee"
                                                    name="cancel_fee"
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    className="form-control"
                                                    value={formData.cancel_fee}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                            <div className="form-group col-sm-6">
                                                <label htmlFor="invoice_terms_days">Invoice Terms (days)</label>
                                                <input
                                                    id="invoice_terms_days"
                                                    name="invoice_terms_days"
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    className="form-control"
                                                    value={formData.invoice_terms_days}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="custom-control custom-checkbox mb-2">
                                            <input
                                                id="allow_card_payments"
                                                name="allow_card_payments"
                                                type="checkbox"
                                                className="custom-control-input"
                                                checked={formData.allow_card_payments}
                                                onChange={handleChange}
                                            />
                                            <label className="custom-control-label" htmlFor="allow_card_payments">
                                                Allow card payments
                                            </label>
                                        </div>
                                        <div className="custom-control custom-checkbox">
                                            <input
                                                id="allow_invoice_payments"
                                                name="allow_invoice_payments"
                                                type="checkbox"
                                                className="custom-control-input"
                                                checked={formData.allow_invoice_payments}
                                                onChange={handleChange}
                                            />
                                            <label className="custom-control-label" htmlFor="allow_invoice_payments">
                                                Allow invoice payments
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        <form onSubmit={(event) => handleSectionSubmit(event, 'branding')}>
                            <div className="card mb-3">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <span className="font-weight-bold">Branding and App Configuration</span>
                                    {renderUpdateButton('branding', 'Branding and App Configuration')}
                                </div>
                                <div className="card-body">
                                    <p className="text-muted">
                                        These values use JSON so every existing and future configuration key remains editable.
                                    </p>
                                    <div className="form-row">
                                        {JSON_FIELDS.map(({name, label, help}) => (
                                            <div className="form-group col-lg-6" key={name}>
                                                <label htmlFor={name}>{label} (JSON)</label>
                                                <textarea
                                                    id={name}
                                                    name={name}
                                                    className="form-control text-monospace"
                                                    rows="8"
                                                    value={jsonFields[name]}
                                                    onChange={(event) => setJsonFields((current) => ({
                                                        ...current,
                                                        [name]: event.target.value,
                                                    }))}
                                                    aria-describedby={`${name}-help`}
                                                />
                                                <small id={`${name}-help`} className="form-text text-muted">{help}</small>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="d-flex justify-content-center">
                            <Link to="/whitelabels" className="btn btn-outline-secondary">Back to Whitelabels</Link>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const WhitelabelCompanyWithMaps = () => (
    <GoogleMapsProvider>
        <WhitelabelCompany/>
    </GoogleMapsProvider>
);

export default withAuth(WhitelabelCompanyWithMaps);
